import { useCallback } from 'react';

import type { AsyncFlagEvaluator } from '@opendoor/auth-fe/forms';

import { getFeatureFlag } from '../api';

/**
 * Development/QA localstorage feature flag override.
 *
 * force enabled  -> window.localStorage.setItem('opendoor.cosmos.<flag>', 'true')
 * force disabled -> window.localStorage.setItem('opendoor.cosmos.<flag>', 'false')
 *
 * Returns the BFF's own variant strings ('on' | 'off') so the override path is
 * shape-compatible with what consumers compare against. Only these two values
 * pin an arm; anything else — including an absent key — returns null so real BFF
 * resolution still happens and the override cannot mask it.
 */
export const getLocalFeatureFlagOverrides = (featureFlag: string): string | null => {
  try {
    // Ignore override check on the server
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(`opendoor.cosmos.${featureFlag}`);
    if (stored === 'true') return 'on';
    if (stored === 'false') return 'off';
    return null;
  } catch {
    return null;
  }
};

// Dedupes the standalone GetSeveralFeatureFlags BFF read per (flag, customer)
// for the page session, so multiple gate mounts don't each hit the network.
const featureFlagVariantCache = new Map<string, Promise<string>>();

/**
 * Async evaluator for AsyncFeatureFlagGate. Resolves the BFF variant (honoring
 * localStorage overrides) and reports enabled when the variant is `'on'`.
 */
export const useFeatureFlagEvaluator = (customerUUID = ''): AsyncFlagEvaluator =>
  useCallback(
    async (flag: string) => {
      const cacheKey = `${flag}|${customerUUID}`;
      let variant = featureFlagVariantCache.get(cacheKey);
      if (!variant) {
        // Evict on rejection so a transient failure isn't cached for the session.
        variant = getFeatureFlag(flag, customerUUID).catch((e) => {
          featureFlagVariantCache.delete(cacheKey);
          throw e;
        });
        featureFlagVariantCache.set(cacheKey, variant);
      }
      return (await variant) === 'on';
    },
    [customerUUID],
  );

// Upper bound on how long a gate blocks first paint on the feature-flag read. A
// slow or hung BFF resolves the gate to its off branch instead of
// spinner-locking the page; hard failures already fail safe.
const FLAG_READ_TIMEOUT_MS = 3000;

/**
 * `useFeatureFlagEvaluator` with a deadline. For a gate whose branches are the
 * whole screen: the read resolves flag-off once the bound elapses, so a hung BFF
 * leaves the user on a working page rather than a spinner they can't get past.
 */
export const useTimeBoundedFeatureFlagEvaluator = (customerUUID = ''): AsyncFlagEvaluator => {
  const evaluateFlag = useFeatureFlagEvaluator(customerUUID);
  return useCallback<AsyncFlagEvaluator>(
    (flag) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), FLAG_READ_TIMEOUT_MS);
      });
      return Promise.race([evaluateFlag(flag), timeout]).finally(() => clearTimeout(timer));
    },
    [evaluateFlag],
  );
};
